import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { generateServiceSchema, generateOrganizationSchema, generateFAQSchema, generateBreadcrumbSchema, injectSchema } from '@/lib/swissSchema';
import { updateMetaTags } from '@/utils/seoCore';
import { generateHreflangLinks, updateHreflangLinks } from '@/lib/swissHreflang';
import { SwissLanguage, getCityDisplayName } from '@/lib/swissI18n';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, CheckCircle, Phone, Clock, Star, TrendingUp, Zap, Calculator } from 'lucide-react';

const SwissCityServicePage = () => {
  const { currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const language = currentLanguage as SwissLanguage;
  
  // Extract service and city from pathname
  const pathname = window.location.pathname;
  const cityServiceMatch = pathname.match(/\/(ai-video|webdesign|seo|google-business-profile)\/(zuerich|basel|bern)/);
  const service = cityServiceMatch ? cityServiceMatch[1] : 'ai-video';
  const city = cityServiceMatch ? cityServiceMatch[2] : 'zuerich';
  
  // Multi-city content with local customization
  const cityContent = {
    zuerich: {
      cityNickname: 'Limmatstadt',
      industries: ['FinTech', 'Tech-Startups', 'Banking'],
      districts: ['Zürich-Ost', 'Zürich-West', 'Seefeld', 'Wiedikon'],
      businessHub: 'Technopark',
      financialDistrict: 'Bahnhofstrasse',
      localPartners: 'Swiss Startup Association',
      localCompetition: 'enorme lokale Konkurrenz',
      searchPattern: '"in Zürich" oder "Zürich-Ost/West"'
    },
    basel: {
      cityNickname: 'Pharmastadt',
      industries: ['Pharma', 'Chemie', 'Life Sciences'],
      districts: ['Basel-Stadt', 'Grossbasel', 'Kleinbasel'],
      businessHub: 'Novartis Campus',
      financialDistrict: 'Marktplatz',
      localPartners: 'BaselArea.swiss',
      localCompetition: 'starke Pharma-Konkurrenz',
      searchPattern: '"in Basel" oder "Basel-Stadt"'
    },
    bern: {
      cityNickname: 'Bundesstadt',
      industries: ['Verwaltung', 'Beratung', 'Bundesbetriebe'],
      districts: ['Bern-Zentrum', 'Kirchenfeld', 'Länggasse'],
      businessHub: 'Wankdorf',
      financialDistrict: 'Bundesgasse',
      localPartners: 'Bern Economic Development',
      localCompetition: 'hohe Verwaltungs-Standards',
      searchPattern: '"in Bern" oder "Bern-Zentrum"'
    }
  };

  const serviceContent = {
    'ai-video': {
      titles: {
        zuerich: 'AI-Video Zürich – Mehr Conversions in 14 Tagen | Future Level',
        basel: 'AI-Video Basel – KI-Videos für Pharma & Life Sciences | Future Level',
        bern: 'AI-Video Bern – Professionelle KI-Videos für Bundesstadt | Future Level'
      },
      descriptions: {
        zuerich: 'AI-Video Produktion in Zürich: KI-gestützte Videos für mehr Conversions. Schnelle Produktion, A/B-Testing, Untertitel DE/FR/IT/EN. Jetzt kostenloses Konzept sichern.',
        basel: 'AI-Video Produktion Basel: KI-Videos für Pharma, Life Sciences und Basler Unternehmen. Compliance-konforme Produktion, mehrsprachig. Kostenloses Konzept anfordern.',
        bern: 'AI-Video Produktion Bern: KI-gestützte Videos für Verwaltung, Beratung und Berner Unternehmen. Professionell, bundesstadt-gerecht. Jetzt Konzept sichern.'
      },
      localProofs: {
        zuerich: [
          'Bereits 47+ Zürcher Unternehmen vertrauen uns',
          'Durchschnittlich +180% mehr Conversions in Zürich',
          'Partner von Swiss Startup Association'
        ],
        basel: [
          'Bereits 23+ Basler Pharma-Unternehmen',
          'Durchschnittlich +160% mehr Leads in Basel',
          'Partner von BaselArea.swiss'
        ],
        bern: [
          'Bereits 31+ Berner Unternehmen & Verwaltung',
          'Durchschnittlich +145% mehr Engagement in Bern',
          'Partner von Bern Economic Development'
        ]
      },
      specials: {
        zuerich: 'Zürich Business Package: CHF 3\'900 statt CHF 4\'500',
        basel: 'Basel Pharma Package: CHF 4\'200 inkl. Compliance-Check',
        bern: 'Bern Professional Package: CHF 3\'700 für Verwaltung & Beratung'
      }
    },
    
    'webdesign': {
      titles: {
        zuerich: 'Webdesign Zürich – In 14 Tagen live, SEO-ready | Future Level',
        basel: 'Webdesign Basel – Pharma-konforme Websites in 14 Tagen | Future Level',
        bern: 'Webdesign Bern – Professionelle Websites für Bundesstadt | Future Level'
      },
      descriptions: {
        zuerich: 'Webdesign Zürich: Moderne Website-Entwicklung für Zürcher Unternehmen. Design + Technik + SEO aus einer Hand. Mehrsprachig, blitzschnell. Gratis Audit anfordern.',
        basel: 'Webdesign Basel: Pharma-konforme Website-Entwicklung für Basler Unternehmen. Compliance, mehrsprachig, life-sciences-ready. Kostenloses Audit anfordern.',
        bern: 'Webdesign Bern: Professionelle Website-Entwicklung für Berner Unternehmen. Verwaltungs-Standards, bundesstadt-gerecht. Gratis Audit anfordern.'
      },
      localProofs: {
        zuerich: [
          '89+ Websites für Zürcher Unternehmen',
          'Durchschnittlich 4.8/5 Sterne von Zürich-Kunden',
          'Spezialist für Zürich-Ost bis Zürich-West'
        ],
        basel: [
          '45+ Websites für Basler Pharma & Life Sciences',
          'Durchschnittlich 4.9/5 Sterne von Basel-Kunden',
          'Compliance-Spezialist für Basel-Stadt'
        ],
        bern: [
          '62+ Websites für Berner Verwaltung & Beratung',
          'Durchschnittlich 4.7/5 Sterne von Bern-Kunden',
          'Standards-Spezialist für Bundesstadt'
        ]
      },
      specials: {
        zuerich: 'Zürich-Paket: Website + lokales SEO für CHF 5\'900',
        basel: 'Basel-Paket: Website + Pharma-Compliance für CHF 6\'500',
        bern: 'Bern-Paket: Website + Verwaltungs-Standards für CHF 5\'700'
      }
    },
    
    'seo': {
      titles: {
        zuerich: 'SEO Agentur Zürich – Technisches & Multilingual SEO | Future Level',
        basel: 'SEO Agentur Basel – Pharma SEO & Life Sciences | Future Level',
        bern: 'SEO Agentur Bern – SEO für Verwaltung & Beratung | Future Level'
      },
      descriptions: {
        zuerich: 'SEO Agentur Zürich: de-CH, fr-CH, it-CH Optimierung. Technisches SEO, Content, Backlinks. Sichtbar in der ganzen Schweiz. Strategiegespräch buchen.',
        basel: 'SEO Agentur Basel: Pharma & Life Sciences SEO. Compliance-konforme Optimierung, multilingual. Basel-Stadt bis schweizweit. Strategiegespräch buchen.',
        bern: 'SEO Agentur Bern: Verwaltungs- und Beratungs-SEO. Professionelle Standards, bundesstadt-gerecht. Bern bis schweizweit. Strategiegespräch buchen.'
      },
      localProofs: {
        zuerich: [
          '156+ Zürcher Unternehmen auf Seite 1',
          'Durchschnittlich +240% organischer Traffic in Zürich',
          'Spezialist für Zürich + schweizweite Expansion'
        ],
        basel: [
          '78+ Basler Pharma-Unternehmen auf Seite 1',
          'Durchschnittlich +220% organischer Traffic in Basel',
          'Spezialist für Basel + Life Sciences SEO'
        ],
        bern: [
          '94+ Berner Verwaltung & Beratung auf Seite 1',
          'Durchschnittlich +200% organischer Traffic in Bern',
          'Spezialist für Bern + Verwaltungs-SEO'
        ]
      },
      specials: {
        zuerich: 'Zürich SEO-Paket: Lokales + nationales SEO für CHF 3\'500/Monat',
        basel: 'Basel SEO-Paket: Pharma + Life Sciences SEO für CHF 3\'800/Monat',
        bern: 'Bern SEO-Paket: Verwaltungs + Beratungs-SEO für CHF 3\'300/Monat'
      }
    },
    
    'google-business-profile': {
      titles: {
        zuerich: 'Google Unternehmensprofil Zürich – Mehr Anrufe & Bewertungen | Future Level',
        basel: 'Google Unternehmensprofil Basel – Pharma Local SEO | Future Level',
        bern: 'Google Unternehmensprofil Bern – Bundesstadt Sichtbarkeit | Future Level'
      },
      descriptions: {
        zuerich: 'Google Business Profile Optimierung Zürich: Lokale Sichtbarkeit steigern, Rezensionen aufbauen, Posts & Angebote managen. Jetzt Setup & Optimierung sichern.',
        basel: 'Google Business Profile Optimierung Basel: Pharma & Life Sciences lokale Sichtbarkeit. Basel-Stadt Optimierung. Setup & Optimierung sichern.',
        bern: 'Google Business Profile Optimierung Bern: Verwaltung & Beratung lokale Sichtbarkeit. Bundesstadt-Standards. Setup & Optimierung sichern.'
      },
      localProofs: {
        zuerich: [
          '234+ Zürcher Unternehmen optimiert',
          'Durchschnittlich +320% mehr Anrufe aus Zürich',
          'Partner von Standort Zürich'
        ],
        basel: [
          '112+ Basler Pharma-Unternehmen optimiert',
          'Durchschnittlich +280% mehr Anrufe aus Basel',
          'Partner von BaselArea.swiss'
        ],
        bern: [
          '145+ Berner Verwaltung & Beratung optimiert',
          'Durchschnittlich +260% mehr Anrufe aus Bern',
          'Partner von Bern Economic Development'
        ]
      },
      specials: {
        zuerich: 'Zürich Local Package: GBP + Citations für CHF 1\'290',
        basel: 'Basel Local Package: GBP + Pharma-Citations für CHF 1\'390',
        bern: 'Bern Local Package: GBP + Verwaltungs-Citations für CHF 1\'240'
      }
    }
  };

  const currentCityData = cityContent[city as keyof typeof cityContent];
  const currentServiceData = serviceContent[service as keyof typeof serviceContent];
  const cityDisplayName = getCityDisplayName('de', city);

  useEffect(() => {
    if (!currentCityData || !currentServiceData) return;

    const title = currentServiceData.titles[city as keyof typeof currentServiceData.titles];
    const description = currentServiceData.descriptions[city as keyof typeof currentServiceData.descriptions];

    // Update meta tags with city-specific content
    updateMetaTags({
      title,
      description,
      canonicalUrl: `https://www.future-level.ch/de/${service}/${city}`,
      language: 'de',
      keywords: `${service}, ${cityDisplayName}, Schweiz, ${service} Agentur ${cityDisplayName}`
    });

    // Update hreflang for city page
    const hreflangLinks = generateHreflangLinks({
      currentLanguage: 'de',
      currentPath: `/${service}/${city}`,
      availableLanguages: ['de', 'fr', 'it', 'en']
    });
    updateHreflangLinks(hreflangLinks);

    // Inject comprehensive schemas
    const orgSchema = generateOrganizationSchema({ language: 'de' });
    const serviceSchema = generateServiceSchema({
      serviceName: title,
      serviceSlug: service,
      city,
      language: 'de',
      priceRange: 'CHF 1500-8500',
      description,
      url: `https://www.future-level.ch/de/${service}/${city}`
    });
    const breadcrumbSchema = generateBreadcrumbSchema('de', service, city);

    injectSchema({
      "@context": "https://schema.org",
      "@graph": [orgSchema, serviceSchema, breadcrumbSchema]
    });
  }, [service, city, currentCityData, currentServiceData]);

  if (!currentCityData || !currentServiceData) {
    return <div>City or service not found</div>;
  }

  const title = currentServiceData.titles[city as keyof typeof currentServiceData.titles];
  const description = currentServiceData.descriptions[city as keyof typeof currentServiceData.descriptions];
  const localProof = currentServiceData.localProofs[city as keyof typeof currentServiceData.localProofs];
  const special = currentServiceData.specials[city as keyof typeof currentServiceData.specials];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-900">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section with City Focus */}
        <section className="relative py-24 overflow-hidden">
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-6 bg-purple-600/20 text-purple-200 border-purple-500/30">
                <MapPin className="w-4 h-4 mr-2" />
                {cityDisplayName} Spezialist • {currentCityData.cityNickname}
              </Badge>
              
              <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                {service.charAt(0).toUpperCase() + service.slice(1).replace('-', ' ')} {cityDisplayName}
                <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent block">
                  {city === 'zuerich' && 'Mehr Conversions in 14 Tagen'}
                  {city === 'basel' && 'Pharma & Life Sciences Expertise'}
                  {city === 'bern' && 'Bundesstadt-Standards'}
                </span>
              </h1>
              
              <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
                {description}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="bg-purple-600 hover:bg-purple-700 text-white">
                  <Phone className="w-5 h-5 mr-2" />
                  Kostenloses {cityDisplayName}-Audit
                </Button>
                {service === 'webdesign' ? (
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-purple-500/30 text-purple-200 hover:bg-purple-900/20"
                    onClick={() => navigate('/de/website-kosten-rechner')}
                  >
                    <Calculator className="w-5 h-5 mr-2" />
                    Website-Kosten berechnen
                  </Button>
                ) : service === 'seo' ? (
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-purple-500/30 text-purple-200 hover:bg-purple-900/20"
                    onClick={() => navigate('/de/seo-roi-rechner')}
                  >
                    <TrendingUp className="w-5 h-5 mr-2" />
                    SEO-ROI berechnen
                  </Button>
                ) : (
                  <Button size="lg" variant="outline" className="border-purple-500/30 text-purple-200 hover:bg-purple-900/20">
                    <Clock className="w-5 h-5 mr-2" />
                    15-Min Beratung buchen
                  </Button>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* City Local Proof Section */}
        <section className="py-16 bg-black/20">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-white mb-8">
                Warum {cityDisplayName}er Unternehmen uns vertrauen
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8">
                {localProof.map((proof, index) => (
                  <Card key={index} className="bg-gradient-to-b from-slate-800/50 to-slate-900/50 border-slate-700">
                    <CardContent className="p-6 text-center">
                      <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                      <p className="text-gray-300">{proof}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              {/* City Special Offer */}
              <div className="mt-12 p-6 bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-lg border border-purple-500/30">
                <h3 className="text-xl font-bold text-white mb-2">
                  <Zap className="inline w-5 h-5 mr-2 text-yellow-400" />
                  Exklusiv für {cityDisplayName}
                </h3>
                <p className="text-purple-200 text-lg">{special}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Industry Focus Section */}
        <section className="py-16">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-white mb-8">
                {cityDisplayName} Branchenfokus
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                {currentCityData.industries.map((industry, index) => (
                  <Badge key={index} className="bg-blue-600/20 text-blue-200 border-blue-500/30 text-base py-2 px-4">
                    {industry}
                  </Badge>
                ))}
              </div>
              <p className="text-gray-300 mt-6">
                Spezialisiert auf {currentCityData.industries.join(', ')} in {cityDisplayName}
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-purple-600 to-pink-600">
          <div className="container mx-auto px-6 text-center">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                Bereit für {cityDisplayName}-Erfolg?
              </h2>
              <p className="text-xl text-purple-100 mb-8">
                Kostenloses Audit speziell für {cityDisplayName}er Unternehmen. Von der {currentCityData.cityNickname} für die {currentCityData.cityNickname}.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="bg-white text-purple-600 hover:bg-gray-100">
                  <MapPin className="w-5 h-5 mr-2" />
                  {cityDisplayName}-Audit anfordern
                </Button>
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  <Star className="w-5 h-5 mr-2" />
                  {cityDisplayName}-Referenzen ansehen
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default SwissCityServicePage;